import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { Group, Tabs } from "@mantine/core";

import { useAtomValue } from "jotai";

import { AsyncState, PageHeader, RegisterNotFound } from "@prostocheck/controls-web";
import { filterReceipts, useCashRegister, useReceipts, useZReports } from "@prostocheck/data-web";
import {
  receiptsAtom,
  receiptsErrorAtom,
  receiptsLoadingAtom,
  zReportsAtom,
  zReportsErrorAtom,
  zReportsLoadingAtom,
} from "@prostocheck/shared";
import { Button, CashRegisterBadge } from "@prostocheck/ui";

import { TRegisterTab } from "./cash-register.models";
import { toDefaultRegisterFilters, useRegisterFilters, useRegisterModals } from "./hooks";
import {
  FiltersPanel,
  PeriodReportModal,
  ReceiptModal,
  ReceiptsPanel,
  ZReportModal,
  ZReportsPanel,
} from "./parts";

export const CashRegisterPage = () => {
  const { t } = useTranslation();
  const { cashRegisterFn } = useParams();
  const register = useCashRegister({ fiscalNumber: cashRegisterFn });

  const { form, range } = useRegisterFilters();
  const { getReceipts } = useReceipts(range);
  const { getZReports } = useZReports(range);
  const {
    receiptFn,
    zReportFn,
    isPeriodReportOpen,
    openReceipt,
    openZReport,
    openPeriodReport,
    closeModal,
  } = useRegisterModals();

  const receiptsList = useAtomValue(receiptsAtom);
  const receiptsLoading = useAtomValue(receiptsLoadingAtom);
  const receiptsError = useAtomValue(receiptsErrorAtom);
  const reports = useAtomValue(zReportsAtom);
  const zReportsLoading = useAtomValue(zReportsLoadingAtom);
  const zReportsError = useAtomValue(zReportsErrorAtom);

  const receipts = useMemo(
    () => filterReceipts({ receipts: receiptsList, filters: form.values }),
    [receiptsList, form.values],
  );

  const [activeTab, setActiveTab] = useState<TRegisterTab>("receipts");
  const isFirstRender = useRef(true);
  // `closeModal` comes from `useSearchParams`, which is not guaranteed to
  // keep its identity across renders — read the latest one from a ref rather
  // than depending on it directly, or the effect below re-runs (and closes
  // the modal it was meant to guard) on renders that only opened one.
  const closeModalRef = useRef(closeModal);
  closeModalRef.current = closeModal;

  // Two separate effects, not one: `getReceipts` and `getZReports` resolve
  // independently, and each sets its own cache key when it does — combining
  // them in one effect would re-invoke both every time either one settles,
  // racing a second real fetch for whichever hasn't resolved yet.
  useEffect(() => {
    if (!register) return;

    void getReceipts(register.fiscalNumber);
  }, [register, getReceipts]);

  useEffect(() => {
    if (!register) return;

    void getZReports(register.fiscalNumber);
  }, [register, getZReports]);

  // Switching registers from the sidebar keeps this page mounted — the
  // preview/modal state belongs to the register that was just left behind.
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;

      return;
    }

    setActiveTab("receipts");
    closeModalRef.current();
  }, [cashRegisterFn]);

  // A `?zReport=` deep link opens on the Z-reports tab.
  useEffect(() => {
    if (!zReportFn || zReportsLoading) return;

    if (reports.some(report => report.fiscalNumber === zReportFn)) {
      setActiveTab("zReports");
    }
  }, [zReportFn, reports, zReportsLoading]);

  if (!register) return <RegisterNotFound />;

  return (
    <>
      <PageHeader
        title={register.name}
        description={t("register.description", {
          fiscalNumber: register.fiscalNumber,
          address: register.address,
        })}
        actions={
          <Group gap="sm">
            <CashRegisterBadge state={register.state} />
            <Button
              variant="secondary"
              onClick={() => {
                form.setValues(toDefaultRegisterFilters());
                form.clearErrors();
              }}
            >
              {t("register.resetFilters")}
            </Button>
          </Group>
        }
      />

      <FiltersPanel form={form} />

      <Tabs
        value={activeTab}
        onChange={value => value && setActiveTab(value as TRegisterTab)}
      >
        <Tabs.List mb="md">
          <Tabs.Tab value="receipts">{t("register.receipts")}</Tabs.Tab>
          <Tabs.Tab value="zReports">{t("register.zReports")}</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="receipts">
          {receiptsLoading || receiptsError ? (
            <AsyncState
              isLoading={receiptsLoading}
              errorResult={receiptsError}
              onRetry={() => getReceipts(register.fiscalNumber)}
            />
          ) : (
            <ReceiptsPanel
              receipts={receipts}
              range={range}
              onSelectReceipt={openReceipt}
            />
          )}
        </Tabs.Panel>

        <Tabs.Panel value="zReports">
          {zReportsLoading || zReportsError ? (
            <AsyncState
              isLoading={zReportsLoading}
              errorResult={zReportsError}
              onRetry={() => getZReports(register.fiscalNumber)}
            />
          ) : (
            <ZReportsPanel
              reports={reports}
              range={range}
              onSelectReport={openZReport}
              onOpenPeriodReport={openPeriodReport}
            />
          )}
        </Tabs.Panel>
      </Tabs>

      <ZReportModal
        fiscalNumber={zReportFn}
        reports={reports}
        isLoading={zReportsLoading}
        onClose={closeModal}
      />
      <PeriodReportModal
        isOpen={isPeriodReportOpen}
        reports={reports}
        range={range}
        cashRegisterFn={register.fiscalNumber}
        onClose={closeModal}
      />
      <ReceiptModal
        fiscalNumber={receiptFn}
        cashRegisterFn={register.fiscalNumber}
        registerName={register.name}
        onClose={closeModal}
      />
    </>
  );
};
